import {
  Cell,
  LayoutBand,
  Grid,
  H2,
  Paragraph,
  Separator,
  Divider,
  Expander,
  ExpanderContent,
  useContainerWidth,
  colors,
  Card,
  Row,
} from '@fs/zion-ui'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Trans } from 'react-i18next/icu.macro'
import { useFeatureFlag } from '@fs/zion-flags'
import {
  ActivitySoccer,
  DiscoveryHeritage,
  DiscoveryTradition,
  DocumentStory,
  EventBarBatMitzvah,
  EventBirth,
  EventOccupation,
  HelpAnnouncement,
  HelpTip,
  PersonFamily,
  PlaceCourt,
  SocialFaceSad,
  SocialLove,
} from '@fs/zion-icon'
import { TreeAppButtons } from '@fs/zion-mobile-app-buttons'

import ExpanderHeading from '../../../shared/ExpanderHeading'

type IconComponent = React.ComponentType<any>

type QuestionCategoryProps = Readonly<{
  categoryKey: string
  icon: IconComponent
  initiallyOpen?: boolean
}>

const QuestionCategory = ({ categoryKey, icon: IconComponent, initiallyOpen = false }: QuestionCategoryProps) => {
  const { t } = useTranslation()
  const atWidth = useContainerWidth()

  const title = (
    <Row as="span" ChildWrapper="span" gutters="sm" alignY="middle">
      <IconComponent size="sm" color={colors.green50} aria-hidden />
      {t(`tree-overview.stories-memories.${categoryKey}.title`, categoryKey)}
    </Row>
  )

  // Get the number of questions for this category
  const questionKeys: string[] = []
  let index = 1
  let key = `tree-overview.stories-memories.${categoryKey}.question${index}`
  let translation = t(key, '')
  while (translation && translation !== key) {
    questionKeys.push(key)
    index++
    key = `tree-overview.stories-memories.${categoryKey}.question${index}`
    translation = t(key, '')
  }

  return (
    <>
      <Divider />
      <Separator size={atWidth({ default: 'sm', lg: 'nano' })} />
      <Expander initiallyOpen={initiallyOpen} linkName={`familyInterviewQuestionsCategory:${categoryKey}`}>
        <ExpanderHeading size="H6">{title}</ExpanderHeading>
        <ExpanderContent>
          <Separator size="xs" />
          <ul style={{ marginInlineStart: '0.5rem', paddingInlineStart: 0 }}>
            {questionKeys.map((questionKey) => (
              <li key={questionKey} style={{ marginBottom: '0.5rem' }}>
                <Paragraph size="sm">{t(questionKey)}</Paragraph>
              </li>
            ))}
          </ul>
        </ExpanderContent>
      </Expander>
    </>
  )
}

export default function StoriesAndMemoriesSection() {
  const atWidth = useContainerWidth()
  const { t } = useTranslation()
  // The list labels are new strings with no translations yet, so they stay behind a flag
  // until Localyzer delivers them. The list markup itself needs no translation and ships now.
  const { isOn: listLabelsOn } = useFeatureFlag('homelands_treeOverview_questionCategoryListLabels') || {}

  return (
    <LayoutBand maxWidth="xxl" marginX={atWidth({ default: 'sm', lg: 'xxl' })} marginY="xxl">
      <H2>{t('tree-overview.stories-memories.heading', 'Stories & Memories to Preserve')}</H2>
      <Separator size="sm" />
      <Paragraph size="md">
        {t(
          'tree-overview.stories-memories.intro',
          'Here are some questions you can ask your family members. Remember to take good notes, or even use the FamilySearch Family Tree app to record their responses directly to FamilySearch.'
        )}
      </Paragraph>
      <Separator size="lg" />

      {/* Two-column grid that stacks on mobile. The categories are split across two Cells, so they are
          exposed as two labelled lists rather than one: a single list spanning both columns would need
          CSS multi-column layout, which reflows items between columns every time an expander opens. */}
      <Grid gutters={atWidth({ default: 'none', lg: 'lg' })}>
        {/* Left column */}
        <Cell columns={atWidth({ default: 12, lg: 6 })}>
          <Grid
            aria-label={
              listLabelsOn
                ? t('tree-overview.stories-memories.categories-list1.label', 'Question categories, part 1 of 2')
                : undefined
            }
            asList
            gutters={atWidth({ default: 'sm', lg: 'pico' })}
          >
            <QuestionCategory categoryKey="childhood-memories" icon={PersonFamily} initiallyOpen />
            <QuestionCategory categoryKey="family-traditions" icon={DiscoveryTradition} />
            <QuestionCategory categoryKey="love-marriage" icon={SocialLove} />
            <QuestionCategory categoryKey="milestones-achievements" icon={EventBirth} />
            <QuestionCategory categoryKey="hobbies-interests" icon={ActivitySoccer} />
            <QuestionCategory categoryKey="funny-stories" icon={HelpAnnouncement} />
            <QuestionCategory categoryKey="hard-times-lessons" icon={SocialFaceSad} />
          </Grid>

          {/* As Grid children these picked up the Grid's gutter; outside the list they do not,
              so the first Separator restores it. */}
          <Separator size={atWidth({ default: 'sm', lg: 'pico' })} />
          {atWidth({ default: <Separator size="nano" />, lg: <Divider /> })}
        </Cell>

        {/* Right column */}
        <Cell columns={atWidth({ default: 12, lg: 6 })}>
          <Grid
            aria-label={
              listLabelsOn
                ? t('tree-overview.stories-memories.categories-list2.label', 'Question categories, part 2 of 2')
                : undefined
            }
            asList
            gutters={atWidth({ default: 'sm', lg: 'pico' })}
          >
            <QuestionCategory categoryKey="daily-life-work" icon={EventOccupation} />
            <QuestionCategory categoryKey="cultural-heritage" icon={DiscoveryHeritage} />
            <QuestionCategory categoryKey="religious-beliefs" icon={EventBarBatMitzvah} />
            <QuestionCategory categoryKey="advice-wisdom" icon={HelpTip} />
            <QuestionCategory categoryKey="historical-events" icon={PlaceCourt} />
            <QuestionCategory categoryKey="stories-ancestors" icon={DocumentStory} />
          </Grid>

          <Separator size={atWidth({ default: 'sm', lg: 'pico' })} />
          <Divider />
        </Cell>
      </Grid>

      <Separator size="lg" />

      {/* Bottom callout */}
      <Row alignX="center">
        <Card backgroundColor={colors.green05} margins="md" maxWidth={1000}>
          <Row gutters="lg" wrap={atWidth({ default: true, lg: false })} alignY="middle">
            <Paragraph size="sm" columns={atWidth({ default: 12, lg: 8 })} growX>
              <Trans
                i18nKey="tree-overview.stories-memories.callout"
                defaults="<0>Remember:</0> These conversations are precious. Take your time, listen carefully, and preserve these stories for future generations."
                components={[<strong key="0" />]}
              />
            </Paragraph>

            <Cell noShrinkX>
              <Paragraph centered={atWidth({ lg: true })} size="md">
                <strong>{t('tree-overview.family-interview.tip.download-heading', 'Download the App')}</strong>
              </Paragraph>
              <Separator size="xs" />
              <TreeAppButtons stacked={atWidth({ default: true, lg: false })} centered={atWidth({ lg: true })} />
            </Cell>
          </Row>
        </Card>
      </Row>
    </LayoutBand>
  )
}
